import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Tab,
  Tabs,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  AttachMoney as DollarSign,
  People as Users,
  CalendarToday as Calendar,
  Settings,
  Refresh as RefreshCw,
  CardGiftcard as Gift,
  TrendingUp,
  AccessTime as Clock,
  CheckCircle
} from '@mui/icons-material';
import api, { subscriptionTokenService, userService } from '../services/api';

interface TokenSetting {
  plan_type: string;
  monthly_tokens: number;
  bonus_tokens: number;
  is_active: boolean;
}

interface AllocationHistory {
  id: number;
  allocated_date: string;
  users_count: number;
  total_tokens: number;
  status: string;
  error_message?: string;
  created_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SubscriptionTokens: React.FC = () => {
  const [settings, setSettings] = useState<TokenSetting[]>([]);
  const [history, setHistory] = useState<AllocationHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [allocating, setAllocating] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [tabValue, setTabValue] = useState(0);

  const planLabels: { [key: string]: { name: string; color: string; icon: string } } = {
    basic: { name: 'Basic (R99/month)', color: '#2196f3', icon: '⭐' },
    premium: { name: 'Premium (R199/month)', color: '#9c27b0', icon: '💎' },
    vip: { name: 'VIP (R399/month)', color: '#ffc107', icon: '👑' },
    agent: { name: 'Agent Account', color: '#4caf50', icon: '🏢' },
    admin: { name: 'Admin Account', color: '#f44336', icon: '🛡️' }
  };

  useEffect(() => {
    fetchSettings();
    fetchHistory();
    fetchUsers();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await subscriptionTokenService.getTokenSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Fallback to default settings on error
      setSettings([
        { plan_type: 'basic', monthly_tokens: 50, bonus_tokens: 0, is_active: true },
        { plan_type: 'premium', monthly_tokens: 150, bonus_tokens: 0, is_active: true },
        { plan_type: 'vip', monthly_tokens: 500, bonus_tokens: 0, is_active: true },
        { plan_type: 'agent', monthly_tokens: 100, bonus_tokens: 0, is_active: true },
        { plan_type: 'admin', monthly_tokens: 1000, bonus_tokens: 0, is_active: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const data = await subscriptionTokenService.getAllocationHistory();
      setHistory(data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await userService.getAll();
      setUsers(data.users || data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSettingChange = (planType: string, field: 'monthly_tokens' | 'bonus_tokens', value: number) => {
    setSettings(prev => prev.map(s => 
      s.plan_type === planType 
        ? { ...s, [field]: value }
        : s
    ));
  };

  const saveSettings = async () => {
    try {
      await subscriptionTokenService.updateTokenSettings(settings);
      alert('Token settings updated successfully');
      fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save token settings');
    }
  };

  const allocateMonthlyTokens = async () => {
    if (!window.confirm('Are you sure you want to allocate monthly tokens to all users?')) {
      return;
    }

    setAllocating(true);
    try {
      const response = await subscriptionTokenService.allocateMonthlyTokens();
      alert(`Successfully allocated ${response.summary.total_tokens} tokens to ${response.summary.users_allocated} users`);
      fetchHistory();
    } catch (error) {
      console.error('Error allocating tokens:', error);
      alert('Failed to allocate monthly tokens');
    } finally {
      setAllocating(false);
    }
  };

  const allocateUserTokens = async () => {
    if (!selectedUser) {
      alert('Please select a user');
      return;
    }

    try {
      const response = await subscriptionTokenService.allocateUserTokens(selectedUser);
      alert(`Allocated ${response.tokens} tokens to user`);
      setSelectedUser('');
    } catch (error) {
      console.error('Error allocating user tokens:', error);
      alert('Failed to allocate tokens to user');
    }
  };

  const getTotalMonthlyTokens = () => {
    return settings.reduce((total, s) => total + s.monthly_tokens + s.bonus_tokens, 0);
  };

  const getLastAllocationDate = () => {
    if (history.length === 0) return 'Never';
    return new Date(history[0].allocated_date).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Subscription Token Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Configure monthly token allocations for each subscription tier
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="success"
          onClick={allocateMonthlyTokens}
          disabled={allocating}
          startIcon={allocating ? <CircularProgress size={20} /> : <Gift />}
        >
          {allocating ? 'Allocating...' : 'Allocate Monthly Tokens'}
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Monthly Tokens
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {getTotalMonthlyTokens()}
                  </Typography>
                </Box>
                <DollarSign sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Active Plans
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {settings.filter(s => s.is_active).length}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Last Allocation
                  </Typography>
                  <Typography variant="h6">
                    {getLastAllocationDate()}
                  </Typography>
                </Box>
                <Calendar sx={{ fontSize: 40, color: 'secondary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Next Allocation
                  </Typography>
                  <Typography variant="h6">
                    1st of Next Month
                  </Typography>
                </Box>
                <Clock sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Token Settings" />
          <Tab label="Allocation History" />
          <Tab label="Individual Allocation" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Subscription Plan</TableCell>
                  <TableCell>Monthly Tokens</TableCell>
                  <TableCell>Bonus Tokens</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {settings.map((setting) => (
                  <TableRow key={setting.plan_type}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6">{planLabels[setting.plan_type]?.icon}</Typography>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {planLabels[setting.plan_type]?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            plan_type: {setting.plan_type}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={setting.monthly_tokens}
                        onChange={(e) => handleSettingChange(
                          setting.plan_type, 
                          'monthly_tokens', 
                          parseInt(e.target.value) || 0
                        )}
                        size="small"
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={setting.bonus_tokens}
                        onChange={(e) => handleSettingChange(
                          setting.plan_type, 
                          'bonus_tokens', 
                          parseInt(e.target.value) || 0
                        )}
                        size="small"
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${setting.monthly_tokens + setting.bonus_tokens} tokens`}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={setting.is_active ? 'Active' : 'Inactive'}
                        color={setting.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={saveSettings}
              startIcon={<Settings />}
            >
              Save Settings
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {history.length === 0 ? (
            <Alert severity="info">
              No allocation history found. Click "Allocate Monthly Tokens" to distribute tokens.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Users</TableCell>
                    <TableCell>Total Tokens</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>
                        {new Date(record.allocated_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{record.users_count}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${record.total_tokens} tokens`}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={record.status === 'completed' ? <CheckCircle /> : undefined}
                          label={record.status}
                          color={
                            record.status === 'completed' ? 'success' :
                            record.status === 'failed' ? 'error' : 'warning'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(record.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Allocate monthly subscription tokens to a specific user immediately. 
            The amount will be based on their current subscription plan.
          </Alert>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                label="Select User"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id.toString()}>
                    {user.firstName} {user.lastName} - {user.email} ({user.role})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              onClick={allocateUserTokens}
              disabled={!selectedUser}
              startIcon={<Gift />}
            >
              Allocate Tokens
            </Button>
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default SubscriptionTokens;